import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { demoStudents, demoWorkLogs, demoPlacements } from "@/data/demoData";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

type SlotPayload = {
  title: string;
  description?: string;
  requirements?: string;
  location?: string;
  duration?: string;
  max_students?: number;
  arrangement?: string;
  pac_allowed?: boolean;
  pac_notes?: string;
};

export function usePartnerStudents() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-students"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoStudents;
      }
      try {
        const res = await apiClient.get("/partners/me/students");
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch partner students:", error);
        throw error;
      }
    },
  });
}

export function usePartnerSlots() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-slots"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoPlacements.map((p) => ({
          id: p.id,
          title: p.role || "Software Intern",
          description: `Work with ${p.partnerName || p.partner_name}`,
          max_students: 5,
          current_students: 2,
          status: p.status,
        }));
      }
      try {
        const res = await apiClient.get("/partners/me/slots");
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch partner slots:", error);
        throw error;
      }
    },
  });
}

export function usePartnerSlot(id?: string | number) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-slot", id],
    enabled: !!id,
    queryFn: async () => {
      if (user?.isDemo) {
        const placement = demoPlacements.find((p) => p.id === id);
        return placement
          ? {
              ...placement,
              title: placement.role || "Software Intern",
              description: `Work with ${placement.partnerName || placement.partner_name}`,
              max_students: 5,
              current_students: 2,
            }
          : null;
      }
      try {
        const res = await apiClient.get(`/partners/me/slots/${id}`);
        return unwrapApiObject(res, null as any);
      } catch (error) {
        console.error("Failed to fetch partner slot:", error);
        throw error;
      }
    },
  });
}

export function useCreateSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: SlotPayload) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), current_students: 0, status: "published" };
      }
      const res = await apiClient.post("/partners/me/slots", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["partner-slots"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["partner-slots"] });
        queryClient.invalidateQueries({ queryKey: ["partner-dashboard"] });
      }
    },
  });
}

export function useUpdateSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: SlotPayload }) => {
      if (user?.isDemo) {
        return { ...data, id };
      }
      const res = await apiClient.patch(`/partners/me/slots/${id}`, data);
      return res.data.data;
    },
    onSuccess: (result, variables) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["partner-slots"], (old: any[]) =>
          old?.map((s: any) =>
            String(s.id) === String(variables.id) ? { ...s, ...result } : s
          )
        );
        queryClient.setQueryData(["partner-slot", variables.id], (old: any) =>
          old ? { ...old, ...result } : result
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["partner-slots"] });
        queryClient.invalidateQueries({ queryKey: ["partner-slot", variables.id] });
      }
    },
  });
}

export function useArchiveSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Slot archived (Demo Mode)" };
      }
      const res = await apiClient.post(`/partners/me/slots/${id}/archive`);
      return res.data.data;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData(["partner-slots"], (old: any[]) =>
          old?.map((s: any) =>
            String(s.id) === String(id) ? { ...s, status: "archived" } : s
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["partner-slots"] });
      }
    },
  });
}

/**
 * One application waiting on this partner, with the student's details attached
 * because the partner is the one deciding on it.
 */
export interface PartnerPlacementRequest {
  id: number;
  slot_id: number;
  status: "submitted" | "accepted" | "declined" | "withdrawn";
  note?: string;
  slot_title?: string;
  location?: string;
  created_at?: string;
  student_id: number;
  student_name?: string;
  student_email?: string;
  university?: string;
  major?: string;
  year_of_study?: number;
  membership_tier?: string;
}

export function usePartnerPlacementRequests() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-placement-requests"],
    queryFn: async (): Promise<PartnerPlacementRequest[]> => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/partners/me/placement-requests");
        return unwrapApiArray<PartnerPlacementRequest>(res);
      } catch (error) {
        console.error("Failed to fetch placement requests:", error);
        throw error;
      }
    },
  });
}

/**
 * Accepting creates the placement itself, so the student, slot and placement
 * lists all change at once.
 */
export function useAcceptPlacementRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (input: { id: number; start_date: number; hours_required: number }) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.post(`/partners/me/placement-requests/${input.id}/accept`, {
        start_date: input.start_date,
        hours_required: input.hours_required,
      });
      return unwrapApiObject(res, null as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-placement-requests"] });
      queryClient.invalidateQueries({ queryKey: ["partner-slots"] });
      queryClient.invalidateQueries({ queryKey: ["partner-students"] });
    },
  });
}

export function useDeclinePlacementRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (input: { id: number; reason?: string }) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.post(`/partners/me/placement-requests/${input.id}/decline`, {
        reason: input.reason || "",
      });
      return unwrapApiObject(res, null as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-placement-requests"] });
    },
  });
}

export function usePartnerWorkLogs() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-work-logs"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoWorkLogs;
      }
      try {
        const res = await apiClient.get("/partners/me/work-logs");
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch partner work logs:", error);
        throw error;
      }
    },
  });
}

export function usePartnerRecommendations() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-recommendations"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/partners/me/recommendations");
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch partner recommendations:", error);
        throw error;
      }
    },
  });
}

export function useCreateRecommendation() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { student_id: number; content: string }) => {
      if (user?.isDemo) {
        return { ...data, id: Date.now(), date: new Date().toISOString().split("T")[0] };
      }
      const res = await apiClient.post("/partners/me/recommendations", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["partner-recommendations"], (old: any[]) =>
          old ? [...old, result] : [result]
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["partner-recommendations"] });
      }
    },
  });
}

export function usePartnerProfile() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["partner-profile"],
    queryFn: async () => {
      if (user?.isDemo) {
        return {
          name: user.name,
          email: user.email,
          company: user.company || "AzamPay",
          industry: "Fintech",
        };
      }
      try {
        const res = await apiClient.get("/partners/me");
        return unwrapApiObject(res);
      } catch (error) {
        console.error("Failed to fetch partner profile:", error);
        throw error;
      }
    },
  });
}

export function useUpdatePartnerProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: Record<string, string>) => {
      if (user?.isDemo) {
        return { ...data, success: true };
      }
      const res = await apiClient.patch("/partners/me", data);
      return res.data.data;
    },
    onSuccess: (result) => {
      if (user?.isDemo && result) {
        queryClient.setQueryData(["partner-profile"], (old: any) =>
          old ? { ...old, ...result } : result
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ["partner-profile"] });
      }
    },
  });
}
